import { IBreadcrumbItem } from "@fluentui/react/lib/Breadcrumb";
import { Location } from "history";
import { encodeRouteId } from "../utils/routeId";

/**
 * Parameters required to build Agile Workstreams breadcrumb
 */
export interface AgileWorkstreamsBreadcrumbParams {
    customerTitle: string;
    appName: string;
    location: Location;
    history: any;
    lastPortfolioIdForChilds: number | null;
    lastProjectIdForChilds: number | null;
}

/**
 * Builds breadcrumb items for Agile Workstreams app
 */
export const buildAgileWorkstreamsBreadcrumb = (
    params: AgileWorkstreamsBreadcrumbParams
): IBreadcrumbItem[] => {

    const {
        customerTitle,
        appName,
        location,
        history,
        lastPortfolioIdForChilds,
        lastProjectIdForChilds,
    } = params;

    const items: IBreadcrumbItem[] = [];

    // --------------------------------------------------------
    // ROOT ITEMS
    // --------------------------------------------------------

    items.push({
        text: 'ZedX Apps',
        key: "root",
        onClick: () => history.push("/")
    });

    items.push({
        text: appName,
        key: "app",
        onClick: () => history.push("/a10/welcome")
    });

    // --------------------------------------------------------
    // DETERMINE CURRENT PATH
    // (supports BrowserRouter & HashRouter)
    // --------------------------------------------------------

    let path = location.pathname || "";
    if ((!path || path === "/") && location.hash) {
        path = location.hash
            .replace(/^#/, "")
            .split("?")[0]
            .split("#")[0];
    }

    // --------------------------------------------------------
    // HELPER ROUTES
    // --------------------------------------------------------

    let workstreamsRoute = `/a10/workflows`;

    if (lastProjectIdForChilds) {
        workstreamsRoute = `/a10/workflows-for-project/${encodeRouteId(lastProjectIdForChilds)}`;
    } else if (lastPortfolioIdForChilds) {
        workstreamsRoute = `/a10/workflows-for-portfolio/${encodeRouteId(lastPortfolioIdForChilds)}`;
    }

    // helper: pushes the Portfolios > Projects breadcrumb chain (as many
    // levels as are actually known) so deeper crumbs (Workstreams, Epics,
    // Sprints, Users, Work Items) don't lose context when navigation came
    // via Portfolio -> Project -> Workstream.
    const pushPortfolioProjectChain = () => {
        if (lastProjectIdForChilds) {
            if (lastPortfolioIdForChilds) {
                items.push({
                    text: "Portfolios",
                    key: "portfolios",
                    onClick: () => history.push("/a10/cwf-portfolios")
                });
                items.push({
                    text: "Projects",
                    key: "projects",
                    onClick: () => history.push(`/a10/cwf-projects-for-portfolio/${encodeRouteId(lastPortfolioIdForChilds)}`)
                });
            }
            else {
                items.push({
                    text: "Projects",
                    key: "projects",
                    onClick: () => history.push("/a10/cwf-projects")
                });
            }
        }
        else if (lastPortfolioIdForChilds) {
            items.push({
                text: "Portfolios",
                key: "portfolios",
                onClick: () => history.push("/a10/cwf-portfolios")
            });
        }
    };

    // --------------------------------------------------------
    // ROUTE-SPECIFIC BREADCRUMB ITEMS
    // --------------------------------------------------------

    if (path === "/a10/welcome") {
        items.push({ text: "Welcome", key: "welcome", isCurrentItem: true });
    }
    else if (path === "/a10/insights") {
        items.push({ text: "Insights", key: "insights", isCurrentItem: true });
    }
    else if (path === "/a10/ai-chats") {
        items.push({ text: "Cloe AI Chats", key: "cloe-ai-chats", isCurrentItem: true });
    }
    else if (path.startsWith("/a10/ask-cloe")) {
        items.push({
            text: "Cloe AI Chats",
            key: "cloe-ai-chats",
            onClick: () => history.push('/a10/ai-chats')
        });
        items.push({ text: "Ask Cloe", key: "ask-cloe", isCurrentItem: true });
    }
    else if (path.startsWith("/a10/cwf-portfolios")) {
        items.push({ text: "Portfolios", key: "portfolios", isCurrentItem: true });
    }
    else if (path === "/a10/cwf-projects") {
        items.push({ text: "Projects", key: "projects", isCurrentItem: true });
    }
    else if (path === "/a10/workflows") {
        items.push({
            text: "Workstreams",
            key: "workstreams",
            isCurrentItem: true
        });
    }
    else if (path.startsWith("/a10/workflows-for-portfolio")) {
        items.push({
            text: "Portfolios",
            key: "portfolios",
            onClick: () => history.push("/a10/cwf-portfolios")
        });
        items.push({
            text: "Workstreams",
            key: "workstreams",
            isCurrentItem: true
        });
    }
    else if (path.startsWith("/a10/workflows-for-project")) {
        items.push({
            text: "Portfolios",
            key: "portfolios",
            onClick: () => history.push("/a10/cwf-portfolios")
        });
        items.push({
            text: "Projects",
            key: "projects",
            onClick: () => history.push(`/a10/cwf-projects-for-portfolio/${encodeRouteId(lastPortfolioIdForChilds!)}`)
        });
        items.push({
            text: "Workstreams",
            key: "workstreams",
            isCurrentItem: true
        });
    }
    else if (path.startsWith("/a10/cwf-projects-for-portfolio")) {
        items.push({
            text: "Portfolios",
            key: "portfolios",
            onClick: () => history.push("/a10/cwf-portfolios")
        });
        items.push({
            text: "Projects",
            key: "projects",
            isCurrentItem: true
        });
    }
    else if (path.startsWith("/a10/epics")) {
        pushPortfolioProjectChain();
        items.push({
            text: "Workstreams",
            key: "workstreams",
            onClick: () => history.push(workstreamsRoute)
        });
        items.push({ text: "Epics", key: "epics", isCurrentItem: true });
    }
    else if (path.startsWith("/a10/sprints")) {
        pushPortfolioProjectChain();
        items.push({
            text: "Workstreams",
            key: "workstreams",
            onClick: () => history.push(workstreamsRoute)
        });
        items.push({ text: "Sprints", key: "sprints", isCurrentItem: true });
    }
    else if (path.startsWith("/a10/workflow-users")) {
        pushPortfolioProjectChain();
        items.push({
            text: "Workstreams",
            key: "workstreams",
            onClick: () => history.push(workstreamsRoute)
        });
        items.push({ text: "Users", key: "users", isCurrentItem: true });
    }
    else if (path.startsWith("/a10/workflow-tasks")) {
        pushPortfolioProjectChain();
        items.push({
            text: "Workstreams",
            key: "workstreams",
            onClick: () => history.push(workstreamsRoute)
        });
        items.push({ text: "Work Items", key: "workitems", isCurrentItem: true });
    }
    else if (path === "/a10/settings/templates") {
        items.push({
            text: "Settings",
            key: "settings",
            onClick: () => history.push("/a10/settings/templates")
        });
        items.push({
            text: "Templates",
            key: "templates",
            isCurrentItem: true
        });
    }
    else if (path.startsWith("/a10/settings/work-item-types")) {
        items.push({
            text: "Settings",
            key: "settings",
            onClick: () => history.push("/a10/settings/templates")
        });
        items.push({
            text: "Templates",
            key: "templates",
            onClick: () => history.push("/a10/settings/templates")
        });
        items.push({
            text: "Work Item Types",
            key: "work-item-types",
            isCurrentItem: true
        });
    }

    return items;
};